import { computed, defineComponent, PropType, provide, reactive, toRefs, watch } from "vue";
import { QTableProps } from 'node_modules/quasar/dist/types/index';
import useQuasar from 'quasar/src/composables/use-quasar.js';;
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { HistoricDetailsResponseDto, HistoricResponseDto, OptionDto, SearchHistoricDto } from "@/dtos";
import { getCurrentDate, getCurrentDateTime, parseToDate, parseToLocalDateTimeFromUtc, warningNotify } from "@/helpers/utilities";
import DatePickerComponent from '@/ui/components/datepickers/DatePickerComponent.vue';
import AdministratorAutocompleteComponent from '@/ui/components/autocompletes/auth/AdministratorAutocompleteComponent.vue';
import TypeOperationSelectComponent from '@/ui/components/selects/TypeOperationSelectComponent.vue';
import { getHistoricOperation } from "@/helpers/options";
import { generateFooterPDF, generateHeaderPDF } from "@/helpers/exports/pdf";
import { formatDataStyledPDF, formatHeaderPDF } from "../export/formats";
import { TableStyle } from "../../tables/enums/type-style";

interface Props {
    show_historic_modal?: boolean;
    id: number | string;
    historic_data: HistoricResponseDto;
    historic_details: HistoricDetailsResponseDto[];
    loading?: boolean;
}

export default defineComponent({
    name: 'HistoricModalComponent',
    components: {
        DatePickerComponent,
        AdministratorAutocompleteComponent,
        TypeOperationSelectComponent
    },
    props: {
        show_historic_modal: {
            type: Boolean
        },
        id: {
            type: [Number, String] as PropType<number | string>,
            required: true
        },
        historic_data: {
            type: Object as () => HistoricResponseDto,
            required: true
        },
        historic_details: {
            type: Object as () => HistoricDetailsResponseDto[],
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    setup(props: Props, { emit }) {
        const toggleHistoricDialog = computed({
            get() { return props.show_historic_modal },
            set(newValue) { emit('update:show_historic_modal', newValue) }
        });

        const config = reactive({
            date_created: parseToLocalDateTimeFromUtc(props.historic_data.date_created.toString()),
            date_updated: (props.historic_data.date_updated) ? parseToLocalDateTimeFromUtc(props.historic_data.date_updated.toString()) : '',
            toggle_details: false,
            label_details: 'Ver Detalles',
            is_historic: true,
            resetData: false
        });

        const historicTable = reactive({
            rows: props.historic_details,
            columns: [
                { name: 'date', label: 'Fecha', field: row => parseToLocalDateTimeFromUtc(row.date), align: "left" },
                { name: 'administrator', label: 'Administrador', field: 'administrator', align: "left" },
                { name: 'operation', label: 'Operación', field: row => getHistoricOperation(row.operation).label, align: "left" },
                { name: 'json', label: 'Información', field: '', align: "left" },
            ] as QTableProps['columns'],
            pagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: props.historic_details.length,
                rowsNumber: props.historic_details.length
            },
            loading: props.loading
        });
        provide('dataTable', historicTable);

        const filters: SearchHistoricDto = reactive({
            id: props.id,
            admin_id: undefined,
            date: undefined,
            operation: undefined
        });

        const $q = useQuasar();

        const toggleDetails = () => {
            if (config.toggle_details) {
                config.toggle_details = false;
                config.label_details = 'Ver Detalles';
                return;
            }

            config.toggle_details = true;
            config.label_details = 'Ocultar Detalles';
            emit('update_filters_historic', filters);
        }

        const toggleHistoricDetails = (result: boolean) => {
            if (result) {
                onReset();
            } else {
                filters.date = getCurrentDateTime();
            }
        }

        const selectDate = (date: string) => {
            filters.date = parseToDate(date);
        }

        const administratorSelected = (opt: OptionDto) => {
            filters.admin_id = opt?.value ?? 0;
        }

        const typeOperationSelected = (opt: OptionDto) => {
            filters.operation = opt.value as any;
        }

        const onSearch = () => {
            emit('update_filters_historic', filters);
        }

        const onReset = () => {
            filters.admin_id = undefined;
            filters.date = undefined;
            filters.operation = undefined;

            config.resetData = !config.resetData;
            emit('update_filters_historic', filters);
        }

        const cancelOption = () => {
            emit('update:show_export_modal', false);
        }

        const onExportHistoric = () => {
            if (historicTable.rows.length <= 0) {
                $q.notify(warningNotify('Por favor seleccione "Ver Detalles" para exportar la información del historial'));
                return;
            }

            const doc = new jsPDF("landscape");
            generateHeaderPDF(doc);

            doc.setFontSize(12);
            doc.text(`Datos de creación`, 20, 50);

            doc.setFont('VisbyRoundCF', 'normal');
            doc.setFontSize(11);
            doc.text(props.historic_data.admin_created, 20, 56);

            doc.setFontSize(11);
            doc.text(config.date_created, 100, 56);

            if (props.historic_data?.date_updated) {
                doc.setFont('VisbyRoundCF', 'bold');
                doc.setFontSize(12);
                doc.text(`Datos última modificación`, 145, 50);

                doc.setFont('VisbyRoundCF', 'normal');
                doc.setFontSize(11);
                doc.text(props.historic_data.admin_updated ?? '', 145, 56);

                doc.setFontSize(11);
                doc.text(config.date_updated, 245, 56);
            }

            if (historicTable.rows.length > 0) {
                const rows = historicTable.rows.map(x => {
                    return {
                        date: parseToLocalDateTimeFromUtc(x.date),
                        administrator: x.administrator,
                        operation: getHistoricOperation(x.operation).label,
                        json: JSON.stringify(x.details)
                    }
                });

                autoTable(doc, {
                    head: formatHeaderPDF(historicTable.columns as any),
                    body: formatDataStyledPDF(historicTable.columns as any, rows),
                    startY: 60,
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    theme: 'grid',
                });
            }

            generateFooterPDF(doc);
            doc.save(`Historial.pdf`);
        }


        watch([() => props.historic_details, () => props.loading], async ([newDetails, newLoading], [oldDetails]) => {
            historicTable.loading = newLoading;

            if (newDetails != oldDetails) {
                historicTable.rows = newDetails;
                historicTable.pagination.rowsNumber = newDetails.length;
                historicTable.pagination.rowsPerPage = newDetails.length;
            }
        });

        return {
            ...toRefs(config),
            ...toRefs(filters),
            ...toRefs(historicTable),
            toggleHistoricDialog,
            cancelOption,
            toggleDetails,
            toggleHistoricDetails,
            selectDate,
            administratorSelected,
            typeOperationSelected,
            onSearch,
            onReset,
            onExportHistoric,
            TableStyle
        }
    }
});